import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Reveal, Overline } from "./primitives";
import { ArrowRight, Loader2, MapPin, MessageCircle } from "lucide-react";
import { APPOINTMENTS, CONTACT } from "@/content/site";
import { trackConversion } from "@/lib/ads";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESSI = APPOINTMENTS;

export function Booking() {
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", interesse: INTERESSI[0], messaggio: "" });
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot anti-spam
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email || !form.telefono) {
      toast.error("Compila nome, email e telefono.");
      return;
    }
    if (!consent) {
      toast.error("Per inviare la richiesta è necessario prendere visione dell'informativa privacy.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consultations`, { ...form, privacy_consent: consent, website });
      setDone(true);
      toast.success("Richiesta inviata. Ti ricontatteremo a breve.");
      setForm({ nome: "", email: "", telefono: "", interesse: INTERESSI[0], messaggio: "" });
      setConsent(false);
    } catch (err) {
      toast.error("Si è verificato un errore. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b border-canvas/30 py-3 text-lg font-light text-canvas placeholder:text-canvas/35 focus:outline-none focus:border-canvas transition-colors duration-300";

  return (
    <section id="prenota" data-testid="section-booking" className="py-24 md:py-40 bg-ink text-canvas">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-5">
          <Reveal><Overline className="text-canvas/70">Prenota una valutazione — 10</Overline></Reveal>
          <Reveal delay={0.1} as="h2" className="mt-7 font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            La prima visita è il punto di partenza.
          </Reveal>
          <Reveal delay={0.2} as="p" className="mt-7 max-w-md text-lg text-canvas/60 leading-relaxed">
            Non sempre il trattamento richiesto inizialmente è quello più adatto. Per questo
            la valutazione medica precede la scelta della tecnologia o del prodotto.
          </Reveal>
          <p className="mt-4 text-sm text-canvas/50 leading-relaxed max-w-md">
            Porta con te eventuali referti, terapie in corso, esami recenti e fotografie precedenti della condizione da trattare.
          </p>
          <div className="mt-12 space-y-4 text-sm text-canvas/70">
            <div className="flex justify-between border-t border-canvas/15 pt-4"><span className="uppercase tracking-[0.15em] text-canvas/50">Studi</span><span className="text-right">{CONTACT.studios.map((s) => <span key={s} className="block">{s}</span>)}</span></div>
            <a href={`mailto:${CONTACT.email}`} className="flex justify-between border-t border-canvas/15 pt-4 hover:text-canvas transition-colors"><span className="uppercase tracking-[0.15em] text-canvas/50">Email</span><span>{CONTACT.email}</span></a>
            <div className="flex justify-between border-t border-canvas/15 pt-4"><span className="uppercase tracking-[0.15em] text-canvas/50">Telefono</span><span className="text-right">{CONTACT.phones.map((p) => <a key={p.href} href={`tel:${p.href}`} onClick={() => trackConversion("call")} className="block hover:text-canvas transition-colors">{p.label}</a>)}</span></div>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            {CONTACT.maps.map((m) => (
              <a
                key={m.url}
                data-testid="maps-link"
                href={m.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-canvas/30 px-4 py-2.5 text-xs uppercase tracking-[0.12em] text-canvas/80 hover:bg-canvas/10 transition-colors"
              >
                <MapPin className="w-3.5 h-3.5" />
                {m.label.split(",")[0]}
              </a>
            ))}
            {CONTACT.whatsapp && (
              <a
                data-testid="whatsapp-link"
                href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(CONTACT.whatsappMsg)}`}
                onClick={() => trackConversion("whatsapp")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-sage/50 px-4 py-2.5 text-xs uppercase tracking-[0.12em] text-sage hover:bg-sage/10 transition-colors"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                WhatsApp
              </a>
            )}
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div data-testid="booking-success" className="border border-canvas/20 p-10 md:p-16 h-full flex flex-col justify-center">
              <span className="font-serif text-6xl text-sage">✦</span>
              <h3 className="mt-6 font-serif text-3xl md:text-4xl font-light">Grazie.</h3>
              <p className="mt-4 text-canvas/60 text-lg max-w-md">La tua richiesta è stata inviata. Il nostro studio ti ricontatterà per fissare la prima valutazione.</p>
              <button data-testid="booking-reset" onClick={() => setDone(false)} className="mt-8 self-start link-underline text-sm text-canvas/80">Invia un’altra richiesta</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="booking-form" className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-9">
              <div className="sm:col-span-1">
                <label className="text-[11px] uppercase tracking-[0.18em] text-canvas/50">Nome e cognome</label>
                <input data-testid="booking-form-name-input" value={form.nome} onChange={set("nome")} className={field} placeholder="Il tuo nome" />
              </div>
              <div className="sm:col-span-1">
                <label className="text-[11px] uppercase tracking-[0.18em] text-canvas/50">Telefono</label>
                <input data-testid="booking-form-phone-input" value={form.telefono} onChange={set("telefono")} className={field} placeholder="+39 ..." />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[11px] uppercase tracking-[0.18em] text-canvas/50">Email</label>
                <input data-testid="booking-form-email-input" type="email" value={form.email} onChange={set("email")} className={field} placeholder="nome@email.it" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[11px] uppercase tracking-[0.18em] text-canvas/50">Tipo di appuntamento</label>
                <select data-testid="booking-form-interest-select" value={form.interesse} onChange={set("interesse")} className={`${field} cursor-pointer appearance-none`}>
                  {INTERESSI.map((i) => <option key={i} value={i} className="bg-ink text-canvas">{i}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-[11px] uppercase tracking-[0.18em] text-canvas/50">Messaggio (facoltativo)</label>
                <textarea data-testid="booking-form-message-input" value={form.messaggio} onChange={set("messaggio")} rows={3} className={`${field} resize-none`} placeholder="Raccontaci il tuo obiettivo" />
                <p className="mt-2 text-xs text-canvas/40 leading-relaxed">Per la tua tutela, non inserire in questo campo dati sanitari o particolari (diagnosi, referti, patologie, fotografie cliniche): verranno raccolti, se necessario, in sede di visita.</p>
              </div>
              {/* honeypot anti-spam: nascosto agli utenti reali */}
              <div className="hidden" aria-hidden="true">
                <label>Non compilare questo campo</label>
                <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
              </div>
              <div className="sm:col-span-2 flex items-start gap-3">
                <input
                  data-testid="booking-form-privacy-checkbox"
                  id="privacy-consent"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-sage cursor-pointer shrink-0"
                />
                <label htmlFor="privacy-consent" className="text-sm text-canvas/60 leading-relaxed cursor-pointer">
                  Ho letto e compreso l'
                  <Link to="/privacy" className="link-underline text-canvas/85">informativa sulla privacy</Link>
                  {" "}e acconsento al trattamento dei miei dati per rispondere alla richiesta.
                </label>
              </div>
              <div className="sm:col-span-2 mt-2">
                <button
                  data-testid="booking-form-submit-button"
                  type="submit"
                  disabled={loading || !consent}
                  className="group relative inline-flex items-center gap-3 overflow-hidden border border-canvas px-9 py-4 text-xs uppercase tracking-[0.2em] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="absolute inset-0 bg-canvas translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
                  <span className="relative flex items-center gap-3 group-hover:text-ink transition-colors duration-500">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    {loading ? "Invio…" : "Invia richiesta"}
                  </span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer data-testid="footer" className="bg-canvas border-t border-ink/10 py-14">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-end justify-between gap-8">
        <div>
          <p className="font-serif text-4xl md:text-5xl font-light tracking-tight">Studio Valle</p>
          <p className="mt-3 text-sm text-muted max-w-sm">Laser, skin quality, tricologia e tecnologie per il corpo. Percorsi medici personalizzati a Genova.</p>
        </div>
        <div className="text-sm text-muted">
          <p>Genova, Italia</p>
          <p className="mt-1">© {new Date().getFullYear()} Studio Valle. Tutti i diritti riservati.</p>
        </div>
      </div>
    </footer>
  );
}
